import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { renderScore, sportColor, sportImage } from "@/lib/sports";
import { useAuth } from "@/context/AuthContext";
import { Trophy, MapPin, Calendar, Wifi, Youtube, Edit3 } from "lucide-react";
import LiveScorer from "@/components/LiveScorer";
import CricketScorer from "@/components/CricketScorer";
import EventTeamsManager from "@/components/EventTeamsManager";
import EventSponsorshipManager from "@/components/sponsorship/EventSponsorshipManager";
import EventScorersManager from "@/components/event/EventScorersManager";
import EventApprovalBanner from "@/components/event/EventApprovalBanner";
import EventPhotoGallery from "@/components/event/EventPhotoGallery";
import FixtureAwardsEditor, { FixtureAwardsBanner } from "@/components/event/FixtureAwardsEditor";
import FixtureMetaEditor, { FixtureMetaStrip } from "@/components/event/FixtureMetaEditor";
import BracketView from "@/components/event/BracketView";
import useFixtureSocket from "@/lib/useFixtureSocket";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const { isAdmin, isPlatformAdmin, isCompanyAdmin, isPlayer, isScorer, companyId, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [standings, setStandings] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [scoringFixture, setScoringFixture] = useState(null);
  const [editingStream, setEditingStream] = useState(false);
  const [editingEvent, setEditingEvent] = useState(false);
  const [eventDraft, setEventDraft] = useState({});
  const [streamDraft, setStreamDraft] = useState("");
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [myScorerAssignment, setMyScorerAssignment] = useState(null);

  const loadAll = async () => {
    const [e, t, f, s, sp] = await Promise.all([
      api.get(`/events/${id}`),
      api.get(`/teams?event_id=${id}`),
      api.get(`/events/${id}/fixtures`),
      api.get(`/events/${id}/standings`),
      api.get(`/sponsors?event_id=${id}`),
    ]);
    setEvent(e.data);
    setTeams(t.data);
    setFixtures(f.data);
    setStandings(s.data);
    setSponsors(sp.data || []);
    setStreamDraft(e.data.stream_url || "");
  };

  useEffect(() => { loadAll(); }, [id]);
  useEffect(() => { if (isPlayer) api.get("/players/me").then((r) => setMyPlayerId(r.data.id)).catch(() => {}); }, [isPlayer]);
  // Scorer: pull their assignments so we can show "Update score" only on the
  // fixtures they've been invited to.
  useEffect(() => {
    if (!isScorer) { setMyScorerAssignment(null); return; }
    api.get("/scorers/me/events").then((r) => {
      const match = (r.data?.events || []).find((x) => x.event?.id === id);
      setMyScorerAssignment(match || null);
    }).catch(() => setMyScorerAssignment(null));
  }, [isScorer, id]);

  // True only for the event's own organiser/HR (or any platform admin) OR the
  // user who originally created the event (covers player-hosted local matches).
  const canManage = !!event && (
    isPlatformAdmin ||
    (user && event.created_by === user.id) ||
    (isCompanyAdmin && companyId && (event.company_id === companyId || (event.companies || []).includes(companyId)))
  );
  // Lock fixture regeneration as soon as any match has started or completed —
  // otherwise live scores would be wiped out by a regenerate.
  const fixturesLocked = fixtures.some((f) => f.status !== "scheduled");
  const canScoreFixture = (fixture) => {
    if (canManage) return true;
    if (!isScorer || !myScorerAssignment) return false;
    if (myScorerAssignment.scope === "all") return true;
    return (myScorerAssignment.fixtures || []).some((f) => f.id === fixture.id);
  };

  // Approval-workflow helpers.
  const approvalStatus = event?.approval_status || "approved";
  const isPendingAck = approvalStatus === "pending_organiser_ack";
  const isPendingAdmin = approvalStatus === "pending_admin_approval";
  const isRejected = approvalStatus === "rejected";
  const isOwnerOrganiser = !!event && user && event.created_by === user.id;

  // Real-time updates: merge incoming fixture changes; refresh standings on completion.
  // The polling fallback re-fetches event fixtures every ~6s when the WebSocket is down.
  useFixtureSocket(
    (payload) => {
      if (!payload || payload.event_id !== id) return;
      setFixtures((prev) => prev.map((f) => (f.id === payload.fixture.id ? payload.fixture : f)));
      if (payload.fixture.status === "completed") {
        api.get(`/events/${id}/standings`).then((r) => setStandings(r.data));
      }
    },
    () => api.get(`/events/${id}/fixtures`).then((r) => setFixtures(r.data)),
  );

  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  // Total sponsors shown in the tab badge = legacy sponsors + marketplace-awarded sponsors
  // (deduped by lowercase name). Mirror the dedupe logic from <EventSponsors/> so the badge
  // count always matches what's actually rendered inside the tab.
  const totalSponsorCount = useMemo(() => {
    const legacyNames = new Set((sponsors || []).map((s) => (s.name || "").toLowerCase()));
    let awarded = 0;
    for (const opp of (event?.sponsorship_opportunities || [])) {
      for (const aw of (opp.awarded_to || [])) {
        if (!legacyNames.has((aw.name || "").toLowerCase())) awarded += 1;
      }
    }
    return (sponsors?.length || 0) + awarded;
  }, [sponsors, event]);

  const myCaptainTeamIds = useMemo(
    () => (myPlayerId ? teams.filter((t) => t.captain_player_id === myPlayerId).map((t) => t.id) : []),
    [teams, myPlayerId]
  );
  const canSeeTeamsTab = isAdmin || canManage || myCaptainTeamIds.length > 0;

  const generate = async () => {
    try {
      await api.post(`/events/${id}/generate-fixtures`);
      toast.success("Fixtures generated");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate fixtures");
    }
  };

  const pairSwissNext = async () => {
    try {
      const { data } = await api.post(`/events/${id}/swiss/pair-next-round`);
      if (data.paired === 0) toast.info(data.message || "All rounds already paired");
      else toast.success(`Round ${data.round}: paired ${data.paired} matches`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to pair next round");
    }
  };

  if (!event) return <div className="bg-[#0a0a0a] min-h-screen text-white"><Nav /><div className="p-20 text-center">Loading…</div></div>;

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen">
      <Nav />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 h-72">
          {(event.banner_url || event.sport) && <img src={event.banner_url || sportImage(event.sport)} className="w-full h-full object-cover opacity-40" alt="" />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/60 to-[#0a0a0a]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10">
          <Link to="/events" className="text-xs font-mono text-neutral-400 hover:text-white">← All events</Link>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border" style={{ borderColor: sportColor(event.sport), color: sportColor(event.sport) }}>{event.sport}</span>
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-sm bg-white/5 text-neutral-300">{event.format.replace("_", " ")}</span>
            <EventStatusBadge status={event.status} />
            {event.is_local_match && (
              <span data-testid="event-local-badge" className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-[#84CC16]/40 bg-[#84CC16]/10 text-[#84CC16]">LOCAL MATCH</span>
            )}
            {event.is_local_match && event.listed_publicly === false && (
              <span data-testid="event-hidden-badge" className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-neutral-500/40 bg-neutral-500/10 text-neutral-400">HIDDEN</span>
            )}
          </div>
          <h1 data-testid="event-title" className="font-display text-6xl tracking-wide mt-4">{event.name}</h1>
          <p className="text-neutral-400 mt-3 max-w-2xl">{event.description}</p>

          {/* Approval-workflow banner — visible to the event creator + platform admin */}
          {(isOwnerOrganiser || isPlatformAdmin) && approvalStatus !== "approved" && (
            <EventApprovalBanner
              event={event}
              isPendingAck={isPendingAck}
              isPendingAdmin={isPendingAdmin}
              isRejected={isRejected}
              isOwnerOrganiser={isOwnerOrganiser}
              isPlatformAdmin={isPlatformAdmin}
              onChange={loadAll}
            />
          )}
          <div className="flex flex-wrap gap-4 mt-4 text-xs font-mono text-neutral-500">
            {event.venue && <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {event.venue}</span>}
            {event.start_date && <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {event.start_date}</span>}
            <span>{teams.length} teams · {fixtures.length} matches</span>
            {event.stream_url ? (
              <a data-testid="event-stream-link" href={event.stream_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#FF3B30] hover:text-white">
                <Youtube className="w-3.5 h-3.5" /> WATCH LIVE
              </a>
            ) : (
              <span data-testid="event-stream-empty" className="flex items-center gap-1.5 text-neutral-600">
                <Wifi className="w-3 h-3" /> NO STREAM SET
              </span>
            )}
          </div>
          {canManage && (
            <div className="mt-6 flex flex-wrap gap-2 items-center">
              <Button
                data-testid="generate-fixtures-btn"
                onClick={generate}
                disabled={fixturesLocked}
                title={fixturesLocked ? "Tournament has already started — fixtures are locked" : undefined}
                className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm disabled:bg-neutral-700 disabled:text-neutral-400 disabled:cursor-not-allowed"
              >
                {fixturesLocked ? "Fixtures locked" : "Generate fixtures"}
              </Button>
              {event.format === "swiss" && fixtures.length > 0 && (
                <Button
                  data-testid="swiss-pair-next-btn"
                  onClick={pairSwissNext}
                  variant="outline"
                  className="border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 rounded-sm"
                >
                  Pair next Swiss round
                </Button>
              )}
              {editingStream ? (
                <div className="flex gap-2 items-center">
                  <Input data-testid="event-stream-input" value={streamDraft} onChange={(e) => setStreamDraft(e.target.value)} placeholder="https://youtube.com/live/…" className="bg-black/40 border-white/10 text-white w-80" />
                  <Button data-testid="event-stream-save" size="sm" onClick={async () => {
                    try { await api.patch(`/events/${id}/stream`, { stream_url: streamDraft }); toast.success("Stream link saved"); setEditingStream(false); loadAll(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }} className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-sm">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingStream(false)} className="text-neutral-400">Cancel</Button>
                </div>
              ) : (
                <Button data-testid="event-stream-edit" size="sm" variant="outline" onClick={() => setEditingStream(true)} className="rounded-sm border-white/10 text-white">
                  <Edit3 className="w-3.5 h-3.5 mr-1" /> {event.stream_url ? "Edit stream link" : "Add stream link"}
                </Button>
              )}
              <Button data-testid="event-edit-btn" size="sm" variant="outline" onClick={() => { setEventDraft({ name: event.name, description: event.description || "", venue: event.venue || "", start_date: event.start_date || "", end_date: event.end_date || "", contact_name: event.contact_name || "", contact_email: event.contact_email || "", contact_phone: event.contact_phone || "" }); setEditingEvent(true); }} className="rounded-sm border-white/10 text-white">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit event
              </Button>
              {event.status !== "cancelled" && (
                <Button
                  data-testid="event-cancel-btn"
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    const reason = window.prompt("Cancel this event? Data (fixtures, teams, awards, photos) is preserved for records. Optional reason:", "");
                    if (reason === null) return; // user hit Cancel on prompt
                    try {
                      await api.post(`/events/${id}/cancel`, { reason });
                      toast.success("Event cancelled");
                      loadAll();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to cancel event");
                    }
                  }}
                  className="rounded-sm border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                >
                  Cancel event
                </Button>
              )}
            </div>
          )}
          {event.status === "cancelled" && (
            <div data-testid="event-cancelled-banner" className="mt-4 border border-[#FF3B30]/40 bg-[#FF3B30]/5 rounded-sm p-3 text-sm text-[#FF3B30]">
              <b>This event has been cancelled.</b>
              {event.cancellation_reason && <span className="text-neutral-400 ml-2">· {event.cancellation_reason}</span>}
            </div>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 pt-6">
        <EventShareAndContact event={event} />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24">
        <Tabs defaultValue="fixtures">
          <TabsList data-testid="event-tabs" className="bg-[#141414] border border-white/10 rounded-sm flex flex-wrap h-auto p-1 justify-start">
            <TabsTrigger value="fixtures" data-testid="tab-fixtures" className="data-[state=active]:bg-[#84CC16] data-[state=active]:text-black rounded-sm">Fixtures</TabsTrigger>
            <TabsTrigger value="standings" data-testid="tab-standings" className="data-[state=active]:bg-[#84CC16] data-[state=active]:text-black rounded-sm">Standings</TabsTrigger>
            {canSeeTeamsTab && <TabsTrigger value="teams" data-testid="tab-teams" className="data-[state=active]:bg-[#84CC16] data-[state=active]:text-black rounded-sm">{["chess", "quiz", "hackathon"].includes(event.sport) ? "Players" : "Teams"}</TabsTrigger>}
            {["knockout", "double_elimination"].includes(event.format) && <TabsTrigger value="bracket" data-testid="tab-bracket" className="data-[state=active]:bg-[#84CC16] data-[state=active]:text-black rounded-sm">Bracket</TabsTrigger>}
            <TabsTrigger value="sponsors" data-testid="tab-sponsors" className="data-[state=active]:bg-[#84CC16] data-[state=active]:text-black rounded-sm">Sponsors ({totalSponsorCount})</TabsTrigger>
            <TabsTrigger value="sponsorship" data-testid="tab-sponsorship" className="data-[state=active]:bg-[#FACC15] data-[state=active]:text-black rounded-sm">Sponsorship</TabsTrigger>
            {canManage && <TabsTrigger value="scorers" data-testid="tab-scorers" className="data-[state=active]:bg-[#06B6D4] data-[state=active]:text-black rounded-sm">Scorers</TabsTrigger>}
          </TabsList>

          <TabsContent value="fixtures" className="mt-6">
            <FixturesList fixtures={fixtures} event={event} teamMap={teamMap} canManage={canManage} canScoreFixture={canScoreFixture} onScore={(f) => setScoringFixture(f)} onAwardsSaved={loadAll} />
          </TabsContent>
          <TabsContent value="standings" className="mt-6">
            <StandingsTable standings={standings} />
          </TabsContent>
          <TabsContent value="teams" className="mt-6">
            <EventTeamsManager event={event} teams={teams} reload={loadAll} />
          </TabsContent>
          {["knockout", "double_elimination"].includes(event.format) && (
            <TabsContent value="bracket" className="mt-6">
              <BracketView fixtures={fixtures} teamMap={teamMap} event={event} />
            </TabsContent>
          )}
          <TabsContent value="sponsors" className="mt-6">
            <EventSponsors event={event} sponsors={sponsors} isAdmin={isAdmin} reload={loadAll} />
          </TabsContent>
          <TabsContent value="sponsorship" className="mt-6">
            <EventSponsorshipManager
              event={event}
              canManage={isPlatformAdmin || (isCompanyAdmin && companyId === event.company_id)}
              reload={loadAll}
            />
          </TabsContent>
          {canManage && (
            <TabsContent value="scorers" className="mt-6">
              <EventScorersManager eventId={event.id} fixtures={fixtures} teamMap={teamMap} />
            </TabsContent>
          )}
        </Tabs>

        <EventPhotoGallery
          event={event}
          canManage={canManage}
          onChange={(photos) => setEvent({ ...event, photos })}
        />
      </div>

      {scoringFixture && event.sport === "cricket" && (
        <CricketScorer
          fixture={scoringFixture}
          event={event}
          teamMap={teamMap}
          onClose={() => setScoringFixture(null)}
          onSaved={loadAll}
        />
      )}

      {scoringFixture && event.sport !== "cricket" && (
        <LiveScorer
          fixture={scoringFixture}
          event={event}
          teamMap={teamMap}
          onClose={() => setScoringFixture(null)}
          onSaved={loadAll}
        />
      )}

      <Dialog open={editingEvent} onOpenChange={setEditingEvent}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white">
          <DialogHeader><DialogTitle>Edit event</DialogTitle></DialogHeader>
          <p className="text-xs text-neutral-500">Sport &amp; format aren&apos;t editable once the event exists (would break fixtures / standings). Use the buttons below for anything else.</p>
          <div className="grid gap-3 mt-2">
            <div><Label className="text-xs font-mono uppercase text-neutral-500">Event name</Label><Input data-testid="event-edit-name" value={eventDraft.name || ""} onChange={(e) => setEventDraft({ ...eventDraft, name: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-500">Description</Label><Input data-testid="event-edit-desc" value={eventDraft.description || ""} onChange={(e) => setEventDraft({ ...eventDraft, description: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-xs font-mono uppercase text-neutral-500">Venue</Label><Input data-testid="event-edit-venue" value={eventDraft.venue || ""} onChange={(e) => setEventDraft({ ...eventDraft, venue: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs font-mono uppercase text-neutral-500">Start date</Label><Input data-testid="event-edit-start" type="date" value={eventDraft.start_date || ""} onChange={(e) => setEventDraft({ ...eventDraft, start_date: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-xs font-mono uppercase text-neutral-500">End date</Label><Input data-testid="event-edit-end" type="date" value={eventDraft.end_date || ""} onChange={(e) => setEventDraft({ ...eventDraft, end_date: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            </div>
            <div className="border-t border-white/10 pt-2 mt-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#EC4899] mb-2">/ Organiser contact (public)</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div><Label className="text-xs font-mono uppercase text-neutral-500">Contact name</Label><Input data-testid="event-edit-cname" value={eventDraft.contact_name || ""} onChange={(e) => setEventDraft({ ...eventDraft, contact_name: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
                <div><Label className="text-xs font-mono uppercase text-neutral-500">Phone / WhatsApp</Label><Input data-testid="event-edit-cphone" value={eventDraft.contact_phone || ""} onChange={(e) => setEventDraft({ ...eventDraft, contact_phone: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
              </div>
              <div><Label className="text-xs font-mono uppercase text-neutral-500">Contact email</Label><Input data-testid="event-edit-cemail" value={eventDraft.contact_email || ""} onChange={(e) => setEventDraft({ ...eventDraft, contact_email: e.target.value })} className="bg-black/40 border-white/10 text-white mt-1" /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditingEvent(false)}>Cancel</Button>
            <Button data-testid="event-edit-save" onClick={async () => {
              try {
                await api.patch(`/events/${id}`, eventDraft);
                toast.success("Event updated");
                setEditingEvent(false);
                loadAll();
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }} className="bg-[#84CC16] hover:bg-[#65A30D] text-black">Save changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function FixturesList({ fixtures, event, teamMap, canManage, canScoreFixture, onScore, onAwardsSaved }) {
  const grouped = fixtures.reduce((acc, f) => {
    (acc[f.round] = acc[f.round] || []).push(f);
    return acc;
  }, {});
  if (!fixtures.length) return <div className="text-neutral-500 text-center py-20">No fixtures yet. {canManage && "Generate them from above."}</div>;
  return (
    <div className="space-y-8">
      {Object.entries(grouped).map(([rnd, list]) => (
        <div key={rnd}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-3">/ Round {rnd}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {list.map((f) => (
              <FixtureCard key={f.id} fixture={f} event={event} teamMap={teamMap} canManage={canManage} canScore={canScoreFixture(f)} onScore={onScore} onAwardsSaved={onAwardsSaved} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FixtureCard({ fixture, event, teamMap, canManage, canScore, onScore, onAwardsSaved }) {
  const a = teamMap[fixture.team_a_id];
  const b = teamMap[fixture.team_b_id];
  const isLive = fixture.status === "live";
  const isCompleted = fixture.status === "completed";
  const [editingAwards, setEditingAwards] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  return (
    <div data-testid={`fixture-card-${fixture.id}`} className={`rounded-sm border bg-[#141414] p-5 hover-lift ${isLive ? "border-[#FF3B30]/50" : "border-white/10"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest">Match #{fixture.match_number}{fixture.bracket_position ? ` · ${fixture.bracket_position}` : ""}</span>
        <StatusPill status={fixture.status} />
      </div>
      <TeamRow team={a} score={renderScore(event.sport, fixture.score?.team_a)} winner={fixture.winner_id === a?.id} />
      <div className="text-[10px] font-mono text-neutral-600 my-1.5 text-center">VS</div>
      <TeamRow team={b} score={renderScore(event.sport, fixture.score?.team_b)} winner={fixture.winner_id === b?.id} />
      <FixtureMetaStrip fixture={fixture} teamMap={teamMap} />
      <FixtureAwardsBanner fixture={fixture} />
      {canScore && a && b && (
        <Button data-testid={`score-fixture-${fixture.id}`} size="sm" onClick={() => onScore(fixture)} className="mt-3 w-full bg-white/5 hover:bg-[#84CC16] text-white rounded-sm border border-white/10">
          Update score
        </Button>
      )}
      {canManage && (
        <Button
          data-testid={`fixture-meta-btn-${fixture.id}`}
          size="sm"
          variant="outline"
          onClick={() => setEditingMeta(true)}
          className="mt-2 w-full bg-transparent hover:bg-[#06B6D4]/10 text-[#06B6D4] rounded-sm border-[#06B6D4]/40"
        >
          Match details (venue · court · toss · officials)
        </Button>
      )}
      {isCompleted && canManage && (
        <Button
          data-testid={`fixture-awards-btn-${fixture.id}`}
          size="sm"
          variant="outline"
          onClick={() => setEditingAwards(true)}
          className="mt-2 w-full bg-transparent hover:bg-[#F59E0B]/10 text-[#F59E0B] rounded-sm border-[#F59E0B]/40"
        >
          {fixture.hero_image_url ? "Edit hero image · view awards" : "Add hero image · view awards"}
        </Button>
      )}
      {a && b && (
        <Link
          to={`/live/${fixture.id}`}
          data-testid={`share-live-${fixture.id}`}
          className="mt-2 block w-full text-center text-[10px] font-mono uppercase text-[#06B6D4] hover:text-white tracking-widest"
        >
          ▶ Open live scorecard
        </Link>
      )}
      {editingAwards && (
        <FixtureAwardsEditor
          fixture={fixture}
          sport={event.sport}
          open={editingAwards}
          onClose={() => setEditingAwards(false)}
          onSaved={onAwardsSaved}
        />
      )}
      {editingMeta && (
        <FixtureMetaEditor
          fixture={fixture}
          event={event}
          teamA={a}
          teamB={b}
          open={editingMeta}
          onClose={() => setEditingMeta(false)}
          onSaved={onAwardsSaved}
        />
      )}
    </div>
  );
}

function TeamRow({ team, score, winner }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <span className="w-2 h-6 rounded-sm" style={{ background: team?.color || "#333" }} />
        <span className={`text-sm ${winner ? "text-white font-semibold" : "text-neutral-300"}`}>{team?.name || "TBD"}</span>
        {winner && <Trophy className="w-3.5 h-3.5 text-[#F59E0B]" />}
      </div>
      <span className="font-mono text-lg">{score}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    scheduled: { label: "SCHEDULED", color: "text-neutral-500 border-white/10" },
    live: { label: "● LIVE", color: "text-[#FF3B30] border-[#FF3B30]/40" },
    completed: { label: "FINAL", color: "text-emerald-400 border-emerald-500/40" },
  };
  const m = map[status] || map.scheduled;
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${m.color}`}>{m.label}</span>;
}

function StandingsTable({ standings }) {
  if (!standings.length) return <div className="text-neutral-500 text-center py-20">No standings yet.</div>;
  return (
    <div className="border border-white/10 rounded-sm overflow-hidden">
      <table data-testid="standings-table" className="w-full text-sm">
        <thead className="bg-[#141414] font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-5 py-3">#</th>
            <th className="text-left px-5 py-3">Team</th>
            <th className="text-right px-3 py-3">P</th>
            <th className="text-right px-3 py-3">W</th>
            <th className="text-right px-3 py-3">D</th>
            <th className="text-right px-3 py-3">L</th>
            <th className="text-right px-5 py-3">PTS</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <tr key={s.team_id} className="border-t border-white/5 hover:bg-white/[0.02]">
              <td className="px-5 py-4 font-mono text-neutral-500">{String(i + 1).padStart(2, "0")}</td>
              <td className="px-5 py-4 flex items-center gap-3">
                <span className="w-1.5 h-6 rounded-sm" style={{ background: s.color }} />
                <span className="font-medium">{s.team_name}</span>
              </td>
              <td className="text-right px-3 font-mono">{s.played}</td>
              <td className="text-right px-3 font-mono text-emerald-400">{s.won}</td>
              <td className="text-right px-3 font-mono">{s.drawn}</td>
              <td className="text-right px-3 font-mono text-[#FF3B30]">{s.lost}</td>
              <td className="text-right px-5 font-mono text-[#84CC16] text-lg font-bold">{s.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TeamsGrid({ teams }) {
  if (!teams.length) return <div className="text-neutral-500 text-center py-20">No teams registered yet.</div>;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {teams.map((t) => (
        <Link to={`/teams/${t.id}`} key={t.id} data-testid={`event-team-card-${t.id}`} className="border border-white/10 rounded-sm p-5 bg-[#141414] hover-lift">
          <div className="flex items-center gap-3">
            <span className="w-2 h-10 rounded-sm" style={{ background: t.color }} />
            <div>
              <div className="font-semibold">{t.name}</div>
              <div className="text-xs font-mono text-neutral-500 uppercase">{t.department}</div>
            </div>
          </div>
          {t.captain && <div className="mt-3 text-xs text-neutral-500">Captain · {t.captain}</div>}
        </Link>
      ))}
    </div>
  );
}

function EventSponsors({ event, sponsors, isAdmin, reload }) {
  const eventId = event.id;
  const [form, setForm] = useState({ name: "", tier: "bronze", logo_url: "", website: "", description: "" });
  const [filter, setFilter] = useState("all"); // all | manual | marketplace
  const tiers = ["title", "gold", "silver", "bronze"];
  const tierColor = { title: "#84CC16", gold: "#F59E0B", silver: "#A3A3A3", bronze: "#A16207" };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.logo_url) return;
    await api.post("/sponsors", { ...form, event_id: eventId });
    setForm({ name: "", tier: "bronze", logo_url: "", website: "", description: "" });
    reload();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove sponsor?")) return;
    await api.delete(`/sponsors/${id}`); reload();
  };

  // Merge marketplace-awarded sponsors into the legacy sponsors list.
  // Tier mapping: opportunity type → display tier on this page. Title slots stay title;
  // associates render as gold; everything else as silver (visually de-emphasised but still credited).
  const TYPE_TO_TIER = { title: "title", associate: "gold", "powered-by": "gold", "category-partner": "gold" };
  const awardedVirtual = [];
  for (const opp of (event.sponsorship_opportunities || [])) {
    for (const aw of (opp.awarded_to || [])) {
      awardedVirtual.push({
        id: `mkt-${aw.interest_id || aw.sponsor_id}`,
        name: aw.name,
        tier: TYPE_TO_TIER[opp.type] || "silver",
        logo_url: aw.logo_url || "",
        website: aw.website || "",
        description: `${opp.name}${aw.industry ? ` · ${aw.industry}` : ""}`,
        _marketplace: true,
      });
    }
  }
  // Dedupe by lowercase name (avoids showing the same brand twice if also added legacy).
  const existingNames = new Set((sponsors || []).map((s) => (s.name || "").toLowerCase()));
  const mergedSponsors = [
    ...(sponsors || []),
    ...awardedVirtual.filter((v) => !existingNames.has((v.name || "").toLowerCase())),
  ];
  const visibleSponsors = mergedSponsors.filter((s) => {
    if (filter === "manual") return !s._marketplace;
    if (filter === "marketplace") return !!s._marketplace;
    return true;
  });
  const marketplaceCount = mergedSponsors.filter((s) => s._marketplace).length;
  const manualCount = mergedSponsors.length - marketplaceCount;

  return (
    <div className="space-y-6">
      {mergedSponsors.length > 0 && (marketplaceCount > 0) && (
        <div className="flex flex-wrap gap-1.5" data-testid="sponsors-filter">
          {[
            { k: "all", label: `All (${mergedSponsors.length})` },
            { k: "manual", label: `Direct (${manualCount})` },
            { k: "marketplace", label: `Via marketplace (${marketplaceCount})` },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              data-testid={`sponsors-filter-${opt.k}`}
              onClick={() => setFilter(opt.k)}
              className={`text-[11px] font-mono uppercase px-2.5 py-1 rounded-sm border transition-colors ${
                filter === opt.k
                  ? "bg-[#FACC15] text-black border-transparent"
                  : "bg-black/40 text-neutral-300 border-white/10 hover:border-white/30"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {tiers.map((t) => {
        const list = visibleSponsors.filter((s) => s.tier === t);
        if (list.length === 0) return null;
        return (
          <div key={t}>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-1 rounded-full" style={{ background: tierColor[t] }} />
              <span className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: tierColor[t] }}>{t}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {list.map((s) => (
                <div key={s.id} data-testid={`event-sponsor-${s.id}`} className="border border-white/10 rounded-sm bg-[#141414] p-4">
                  <div className="flex items-center gap-3">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} className="w-12 h-12 object-contain rounded-sm bg-black/40 p-1" />
                    ) : (
                      <div className="w-12 h-12 rounded-sm bg-black/40 flex items-center justify-center text-[10px] font-mono text-neutral-500 border border-white/10">
                        {(s.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5">
                        {s.name}
                        {s._marketplace && <span className="text-[8px] font-mono uppercase text-[#FACC15] border border-[#FACC15]/40 rounded-sm px-1 py-0.5">via marketplace</span>}
                      </div>
                      <div className="text-[10px] font-mono uppercase text-neutral-500">{s.tier}</div>
                    </div>
                  </div>
                  {s.description && <p className="text-xs text-neutral-400 mt-2 line-clamp-2">{s.description}</p>}
                  {s.website && (
                    <a href={s.website} target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono text-[#FACC15] hover:underline mt-1 inline-block">
                      {s.website.replace(/^https?:\/\//, "").replace(/\/.*/, "")}
                    </a>
                  )}
                  {isAdmin && !s._marketplace && (
                    <Button size="sm" variant="ghost" data-testid={`event-sponsor-del-${s.id}`} onClick={() => remove(s.id)} className="text-[#FF3B30] mt-2">Remove</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {visibleSponsors.length === 0 && <div className="text-neutral-500 text-center py-12 border border-dashed border-white/10 rounded-sm">{mergedSponsors.length === 0 ? "No sponsors for this tournament yet." : "No sponsors match this filter."}</div>}

      {isAdmin && (
        <form onSubmit={add} className="border border-white/10 rounded-sm bg-[#141414] p-5 mt-8 grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2 font-display tracking-wider text-xl">ADD SPONSOR</div>
          <input data-testid="event-sponsor-name" placeholder="Sponsor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-white text-sm" />
          <select data-testid="event-sponsor-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-white text-sm">
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input data-testid="event-sponsor-logo" placeholder="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="md:col-span-2 bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-white text-sm" />
          <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-white text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/40 border border-white/10 rounded-sm px-3 py-2 text-white text-sm" />
          <Button data-testid="event-sponsor-add" type="submit" className="md:col-span-2 bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm">Add sponsor</Button>
        </form>
      )}
    </div>
  );
}


// Universal Share row + Organiser contact card.
// Anyone (even anonymous visitors) can share the event via WhatsApp / X / Copy;
// when contact fields are set, they render a compact contact card so teams can
// reach out to participate.
function EventShareAndContact({ event }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const msg = `Kreeda Nation event: ${event.name}${event.venue ? " · " + event.venue : ""}. ${url}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const tx = `https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`;
  const invite = event.contact_phone
    ? `https://wa.me/${event.contact_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi${event.contact_name ? " " + event.contact_name : ""}! I'd like my team to participate in "${event.name}" on Kreeda Nation. ${url}`)}`
    : null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const hasContact = !!(event.contact_name || event.contact_email || event.contact_phone);

  return (
    <div className="grid md:grid-cols-2 gap-3">
      {/* Share strip — visible to everyone */}
      <div data-testid="event-share-block" className="border border-white/10 bg-[#141414] rounded-sm p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#06B6D4] mb-2">/ Share this event · invite viewers</div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="event-share-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-9 px-3 rounded-sm bg-[#25D366] hover:bg-[#1ea855] text-black font-semibold text-xs">WhatsApp</a>
          <a data-testid="event-share-x" href={tx} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-9 px-3 rounded-sm bg-white hover:bg-neutral-200 text-black font-semibold text-xs">X / Twitter</a>
          <button data-testid="event-share-copy" onClick={copy} className="inline-flex items-center h-9 px-3 rounded-sm bg-[#06B6D4] hover:bg-[#0891B2] text-black font-semibold text-xs">Copy link</button>
        </div>
        <div className="mt-2 text-[10px] font-mono text-neutral-500 break-all">{url}</div>
      </div>

      {/* Organiser contact card — visible only when at least one field is set */}
      {hasContact ? (
        <div data-testid="event-contact-block" className="border border-[#EC4899]/40 bg-[#EC4899]/5 rounded-sm p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#EC4899] mb-2">/ Organiser contact · want to participate?</div>
          {event.contact_name && <div className="text-sm font-semibold">{event.contact_name}</div>}
          {event.contact_phone && <div className="text-xs font-mono text-neutral-300 mt-0.5">{event.contact_phone}</div>}
          {event.contact_email && <a href={`mailto:${event.contact_email}`} className="text-xs font-mono text-[#06B6D4] hover:underline break-all">{event.contact_email}</a>}
          {invite && (
            <a data-testid="event-contact-wa" href={invite} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center h-9 px-3 rounded-sm bg-[#25D366] hover:bg-[#1ea855] text-black font-semibold text-xs">
              WhatsApp the organiser
            </a>
          )}
        </div>
      ) : (
        <div className="border border-white/10 bg-[#141414] rounded-sm p-4 text-xs text-neutral-500 flex items-center justify-center italic">
          Organiser hasn&apos;t added public contact details yet.
        </div>
      )}
    </div>
  );
}
